import type { ApiBudgetAthleteCategorized, ApiTeamBudgetOverviewCategorized } from '@/types/budgets.categorized';
import type { ApiBudgetAthlete } from '@/types/athletes';
import type { FormFields } from '@/composables/useBudgetForm';
import type { TeamBudgetOverview, OrgBudget } from '@/types/budgets';
import { convertCentsToDollarsDecimal } from './helpers';
import type { TeamBudget } from '@/types/budgetGroups';

/**
 * Budget formats that can be transformed:
 * - TeamBudgetOverview/OrgBudget: Old format (camelCase, dollars)
 * - ApiTeamBudgetOverviewCategorized: New format (snake_case, cents)
 */
export type BudgetForTransform
  = | TeamBudgetOverview
  | OrgBudget
  | TeamBudget & { teamTwrn?: string }
  | (ApiTeamBudgetOverviewCategorized & {
    team_twrn?: string;
    team?: { twrn: string };
  });

/**
 * Type guard: Is this the new categorized format?
 * Check for snake_case fields that only exist in new format
 */
export function isCategorizedBudget(
  budget: BudgetForTransform,
): budget is ApiTeamBudgetOverviewCategorized {
  return 'institutional_target' in budget
      || 'third_party_target' in budget
      || 'academic_year_date_id' in budget
      || 'roster_size' in budget
      || 'scholarship_value' in budget;
}

/**
 * Convert ApiBudgetAthleteCategorized to ApiBudgetAthlete format
 * This is needed to bridge the gap between the new categorized budget API
 * and the existing BudgetAthlete class that expects the old format.
 */
export function convertCategorizedToStandardAthlete(
  athlete: ApiBudgetAthleteCategorized,
): ApiBudgetAthlete {
  return {
    ...athlete,
    line_item: {
      allocation: 0, // Not used in categorized budgets
      created_at: athlete.line_item.created_at || null,
      has_roster_spot: athlete.line_item.has_roster_spot,
      nil_earnings: athlete.line_item.nil_earnings || null,
      scholarship_allocation: athlete.line_item.scholarship_allocation || null,
      scholarship_percentage: athlete.line_item.scholarship_percentage || 0,
      updated_at: athlete.line_item.updated_at || null,
    },
  };
}

/**
 * Transforms raw budget data into form-ready fields
 * Handles both old camelCase format and new categorized snake_case format
 *
 * @param rawBudget - The raw budget data from API or class instance
 * @param getBudgetFormFieldsFn - Function to transform budget into basic form fields
 * @param findAcademicYearFn - Function to convert UUID to AcademicYear object
 * @returns Transformed form fields or undefined if no budget provided
 */
export function transformBudgetForForm(
  rawBudget: BudgetForTransform | null | undefined,
  getBudgetFormFieldsFn: (budget: BudgetForTransform) => Record<string, unknown>,
  findAcademicYearFn: (uuid: string) => unknown,
): Partial<FormFields> | undefined {
  if (!rawBudget) return undefined;

  const transformed: Record<string, unknown> = getBudgetFormFieldsFn(rawBudget);

  if (isCategorizedBudget(rawBudget)) {
    // New format: snake_case fields in cents
    // Prefer camelCase if both exist (shouldn't happen in production, but handle for tests)
    const uuid = ('academicYearDateId' in rawBudget ? rawBudget.academicYearDateId as string : undefined)
              || rawBudget.academic_year_date_id;
    if (uuid) {
      transformed.academicYear = findAcademicYearFn(uuid);
    }

    if (rawBudget.institutional_target !== undefined) {
      transformed.totalBudget = convertCentsToDollarsDecimal(rawBudget.institutional_target);
    }
    if (rawBudget.third_party_target !== undefined) {
      transformed.thirdPartyTarget = convertCentsToDollarsDecimal(rawBudget.third_party_target);
    }
    if (rawBudget.scholarship_value !== undefined) {
      transformed.scholarshipValue = convertCentsToDollarsDecimal(rawBudget.scholarship_value);
    }
    if (rawBudget.roster_size !== undefined) {
      transformed.rosterSize = rawBudget.roster_size;
    }
    if (rawBudget.scholarships !== undefined) {
      transformed.scholarships = rawBudget.scholarships / 100;
    }
  } else {
    // Old format: camelCase fields in dollars (TeamBudgetOverview or OrgBudget)
    const uuid = rawBudget.academicYearDateId;
    if (uuid) {
      transformed.academicYear = findAcademicYearFn(uuid);
    }
  }

  return transformed as Partial<FormFields>;
}

/**
 * gets the default budget TWRN for the current date
 * based on the academic year date ID in an array of budgets
 * and the current academic year UUID.
 * Falls back to the first budget when no academic year match is found.
 * @param budgets - array of budgets
 * @param currentAcademicYearUuid - current academic year UUID
 * @returns array of budget TWRNs (single element)
 */
export function getDefaultBudgetTwrnForCurrentDate(
  budgets: Array<{
    twrn: string;
    academicYearDateId: string;
  }> | undefined,
  currentAcademicYearUuid: string | undefined,
): string[] {
  if (!budgets?.length) return [];
  const match = currentAcademicYearUuid
    ? budgets.find((b) => b.academicYearDateId === currentAcademicYearUuid)
    : null;
  const fallback = match ?? budgets[0];
  return fallback ? [fallback.twrn] : [];
}
